/**
 * TemplateBackground Component
 *
 * Unified background component that handles all background type rendering
 * for E-Commerce video templates. Eliminates duplication across templates
 * by providing a single entry point for background rendering.
 *
 * Supports:
 * - Aurora background (northern lights effect)
 * - Animated gradient (3 rotating radial gradients)
 * - Retro grid (80s synthwave perspective grid)
 * - Ripple effect (expanding dot grid)
 * - None (simple gradient fallback)
 */

import React from 'react';
import { AbsoluteFill } from 'remotion';
import type { BackgroundType, GradientPalette, VisualEffectsConfig } from '../types';
import { AuroraBackground } from './AuroraBackground';
import { AnimatedGradientBackground } from './AnimatedGradientBackground';
import type { GradientPalette as GradientPaletteType } from './AnimatedGradientBackground';
import { RetroGridBackground } from './RetroGridBackground';
import { BackgroundRippleEffect } from './BackgroundRippleEffect';
import { FloatingCircles } from './FloatingShapes';
import { AmbientGlow, Bokeh } from './LightEffects';
import { PulsingGlow } from './ShimmerEffect';
import { adjustBrightness, PALETTE_TO_RETRO_GRID } from '../utils';

/**
 * Color theme configuration for decorative effects
 */
export interface TemplateColorTheme {
  /** Primary brand color */
  brandColor: string;
  /** Accent color for highlights */
  accentColor: string;
  /** Optional fallback gradient end color (defaults to darkened brandColor) */
  fallbackGradientEnd?: string;
}

/**
 * Configuration for the ripple effect start frame
 */
export interface RippleConfig {
  /** Frame when ripple animation starts */
  startFrame: number;
}

export interface TemplateBackgroundProps {
  /** Background type to render */
  backgroundType: BackgroundType;
  /** Gradient palette for animated_gradient background */
  backgroundPalette: GradientPalette;
  /** Color theme for the background and effects */
  colorTheme: TemplateColorTheme;
  /** Visual effects configuration */
  effects: VisualEffectsConfig;
  /** Frames per second for timing calculations */
  fps: number;
  /** Children to render on top of the background */
  children: React.ReactNode;
  /** Optional ripple configuration */
  rippleConfig?: RippleConfig;
}

/**
 * Renders decorative background effects based on the effects configuration.
 * These effects overlay on top of the background but below the main content.
 */
function DecorativeEffects({
  effects,
  colorTheme,
  fps,
}: {
  effects: VisualEffectsConfig;
  colorTheme: TemplateColorTheme;
  fps: number;
}): React.ReactElement {
  const { accentColor } = colorTheme;

  return (
    <>
      {/* Floating circles for depth */}
      {effects.floatingShapes && (
        <FloatingCircles
          count={8}
          color="rgba(255, 255, 255, 0.05)"
          speed={0.2}
          startFrame={0}
          seed={12345}
        />
      )}

      {/* Ambient glow from top */}
      {effects.ambientGlow && (
        <AmbientGlow
          color="rgba(255, 255, 255, 0.08)"
          position="top"
          intensity={0.4}
          startFrame={0}
          pulse={true}
        />
      )}

      {/* Soft bokeh in background */}
      {effects.bokeh && (
        <Bokeh
          count={6}
          color="rgba(255, 255, 255, 0.08)"
          intensity={0.5}
          minSize={60}
          maxSize={150}
          startFrame={fps * 0.3}
          seed={54321}
        />
      )}

      {/* Subtle pulsing glow on edges */}
      {effects.pulsingGlow && (
        <PulsingGlow
          color={`${accentColor}33`}
          speed={0.5}
          intensity={0.3}
          startFrame={fps}
          blurRadius={40}
        />
      )}
    </>
  );
}

/**
 * TemplateBackground
 *
 * Unified background component for all E-Commerce video templates.
 * Handles background type switching and decorative effects rendering.
 */
export function TemplateBackground({
  backgroundType,
  backgroundPalette,
  colorTheme,
  effects,
  fps,
  children,
  rippleConfig,
}: TemplateBackgroundProps): React.ReactElement {
  const { brandColor, accentColor, fallbackGradientEnd } = colorTheme;

  // Calculate fallback gradient end color
  const gradientEnd = fallbackGradientEnd || adjustBrightness(brandColor, -30);

  // Aurora colors derived from brand/accent
  const auroraColors = {
    primary: `${accentColor}cc`,
    secondary: `${brandColor}bb`,
    tertiary: 'rgba(255, 255, 255, 0.4)',
    accent: `${accentColor}99`,
  };

  // Ripple start frame (defaults to fps * 0.5 if not provided)
  const rippleStartFrame = rippleConfig?.startFrame ?? fps * 0.5;

  // Combined content with decorative effects
  const contentWithEffects = (
    <>
      <DecorativeEffects effects={effects} colorTheme={colorTheme} fps={fps} />
      {children}
    </>
  );

  switch (backgroundType) {
    case 'aurora':
      return (
        <AuroraBackground
          backgroundColor={brandColor}
          colors={auroraColors}
          animationSpeed={0.8}
          blurAmount={50}
          showRadialGradient={true}
        >
          {contentWithEffects}
        </AuroraBackground>
      );

    case 'animated_gradient':
      return (
        <AnimatedGradientBackground
          palette={backgroundPalette as GradientPaletteType}
          backgroundColor={brandColor}
          loopDuration={10}
          orbitRadius={30}
          blur={100}
          brightness={0.9}
          speed={0.8}
        >
          {contentWithEffects}
        </AnimatedGradientBackground>
      );

    case 'retro_grid':
      return (
        <RetroGridBackground
          preset={PALETTE_TO_RETRO_GRID[backgroundPalette] || 'synthwave'}
          cellSize={50}
          angle={65}
          lineOpacity={0.7}
          speed={1.2}
          fadeHeight={40}
          showHorizonGlow={true}
        >
          {contentWithEffects}
        </RetroGridBackground>
      );

    case 'ripple':
      return (
        <AbsoluteFill
          style={{
            background: `linear-gradient(180deg, ${brandColor} 0%, ${gradientEnd} 100%)`,
          }}
        >
          <AbsoluteFill>
            <BackgroundRippleEffect
              startFrame={rippleStartFrame}
              cols={12}
              rows={20}
              cellSize={12}
              gap={80}
              color={`${accentColor}90`}
              shape="circle"
              animationDuration={1.2}
              delayMultiplier={0.15}
              maxScale={2.5}
              originX={50}
              originY={35}
              fadeOut={false}
              restOpacity={0.08}
              peakOpacity={0.5}
              shadowBlur={15}
              shadowColor={`${accentColor}50`}
            />
          </AbsoluteFill>
          {contentWithEffects}
        </AbsoluteFill>
      );

    case 'none':
    default:
      return (
        <AbsoluteFill
          style={{
            background: `linear-gradient(180deg, ${brandColor} 0%, ${gradientEnd} 100%)`,
          }}
        >
          {contentWithEffects}
        </AbsoluteFill>
      );
  }
}

export default TemplateBackground;
